/**
 * HowToPlay — in-app "How to play" panel. Owner: Phase-4 A4.5.
 *
 * Surfaced as a toggleable overlay from the RoomGate and the Lobby. Closed by
 * default, opens when the user clicks the "?" button. The CONTENT is curated
 * from `docs/RULES.md` highlights (deck/teams/bidding/play/scoring/jokers/
 * end-of-game), with section anchors so the panel scrolls cleanly on phones.
 *
 * Binding contract (DO NOT change without A4.5 sign-off):
 *   - Exported components: `HowToPlay` (the overlay) and `HowToPlayButton`
 *     (the small ⓘ button that toggles it).
 *   - The button calls `onOpen()`; the overlay calls `onClose()`.
 *   - State lives in the parent (RoomGate / Lobby); this keeps the overlay
 *     out of the global store (no need to mutate it for a UI-only toggle).
 *
 * Accessibility (PRD §5.5 FR-U5, RULES-aligned):
 *   - role="dialog" with aria-modal="true", aria-labelledby pointing to the
 *     panel title.
 *   - Escape key + backdrop click close the overlay.
 *   - Focus moves to the close button on open (handled by autoFocus).
 *
 * The sections themselves are filled by A4.5 in the same file — keeping the
 * content beside the component (vs a separate markdown loader) avoids any
 * runtime dep on the RULES.md file at build time.
 */
import { useCallback, useEffect } from "react";

export interface HowToPlayProps {
  open: boolean;
  onClose: () => void;
}

export interface HowToPlayButtonProps {
  onOpen: () => void;
  className?: string;
}

export function HowToPlayButton({
  onOpen,
  className,
}: HowToPlayButtonProps): JSX.Element {
  return (
    <button
      type="button"
      className={["howto-button", className ?? ""].filter(Boolean).join(" ")}
      aria-label="How to play"
      onClick={onOpen}
    >
      ?
    </button>
  );
}

export function HowToPlay({ open, onClose }: HowToPlayProps): JSX.Element | null {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    },
    [onClose],
  );
  useEffect(() => {
    if (!open) return;
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, handleKeyDown]);

  if (!open) return null;

  return (
    <div
      className="howto-backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="howto-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="howto-title"
      >
        <header className="howto-header">
          <h2 id="howto-title" className="howto-title">
            How to play Hookum
          </h2>
          <button
            type="button"
            className="howto-close"
            aria-label="Close how to play"
            onClick={onClose}
            autoFocus
          >
            ✕
          </button>
        </header>
        <div className="howto-body">
          <section className="howto-section">
            <h3>The game</h3>
            <p>
              Hookum is a partnership trick-taking game in the Hokm / Court
              Piece family. You sit with one partner across the table (4-player)
              or two partners flanking opponents (6-player). The team that wins
              more rounds wins the game.
            </p>
          </section>

          <section className="howto-section">
            <h3>Seats &amp; teams</h3>
            <p>
              You sit at the bottom. Turn order goes to your right. In
              4-player, your partner sits across; teammates have the same seat
              parity (both even, or both odd). In 6-player, teammates
              alternate around the table. Hands pool into one shared team
              pile — there is no individual hand count.
            </p>
          </section>

          <section className="howto-section">
            <h3>The deal</h3>
            <ul>
              <li>4-player, jokers off: standard 52, 13 cards each.</li>
              <li>
                4-player, jokers on: 52 + Red + Black joker, then two random
                "2"s are removed and announced. Still 13 each.
              </li>
              <li>6-player: 54 cards (jokers always on), 9 each.</li>
            </ul>
            <p>
              You see an initial hand (5 cards in 4-player, 3 in 6-player) and
              bid on that. After the auction, the rest of the deck is dealt.
              Your hand auto-sorts by suit, high to low; trump cards are
              highlighted in the hand bar.
            </p>
          </section>

          <section className="howto-section">
            <h3>Bidding &amp; trump</h3>
            <p>
              Each round one seat is the "caller" and the role rotates so every
              seat calls once per game. The caller opens with the base bid —
              7 in 4-player, 5 in 6-player — and picks any trump suit. You can
              call a suit you hold no card of; it's a legal play.
            </p>
            <p>
              Once the caller opens, a 10-second auction window starts. Anyone
              except the current high bidder can either raise — pick a higher
              value and any trump suit — or hit the explicit <strong>Pass</strong>
              button to opt out. Every new bid resets the timer to 10 seconds;
              a pass does NOT reset it. Pass is permanent within the same
              auction: once you pass, you can't bid again until next round.
              The ceiling is the total hands in the round.
            </p>
            <p>
              The auction ends three ways: (a) the 10-s timer runs out, (b)
              every non-high-bidder has passed, or (c) the current high bidder
              clicks <strong>Skip auction</strong> after everyone else has
              passed — the high bidder's "Skip" signals they're ready to lock
              in, and the auction ends synchronously. While the auction is
              live the panel shows an "X / N ready" counter (passes + the
              high-bidder's skip toggle) so you can see how close it is to
              ending.
            </p>
            <p>
              The final high bidder's team becomes the contract team and that
              bidder leads hand 1. If your partner overbids you, the contract
              stays with your team at the raised value. If an opponent
              overbids, the contract flips to the other team.
            </p>
          </section>

          <section className="howto-section">
            <h3>Playing a hand</h3>
            <ul>
              <li>
                The lead plays any one card (a joker can never be led — it has
                no suit).
              </li>
              <li>
                If you hold the led suit, you must follow it. No trumping or
                burning while you can follow.
              </li>
              <li>
                If you are void in the led suit, you may cut with a trump or
                burn any other card. Cutting is optional — burning to duck
                when your partner is already winning is a legal play, and
                any trump is a legal cut (it doesn't have to outrank a trump
                already played).
              </li>
              <li>
                A joker may be played at any time, ignoring follow-suit. It
                acts as a super-trump.
              </li>
            </ul>
            <p>
              Hand winner, evaluated in order: highest joker (Red beats
              Black) → highest trump → highest card of the led suit. The
              winner leads next.
            </p>
          </section>

          <section className="howto-section">
            <h3>Joker-lead forfeit</h3>
            <p>
              You just won a hand, but every card left in your hand is a
              joker. Jokers can't be led, so you forfeit every remaining hand
              of the round to the other team — your jokers are discarded
              unplayed and the round ends. This is the only way a round ends
              before all hands are played.
            </p>
          </section>

          <section className="howto-section">
            <h3>Scoring a round</h3>
            <p>
              Call the final bid B and the total hands T. The contract team
              needs exactly B hands to push, or B + 1 to win the round. The
              other team needs T − B to push, or T − B + 1 to win.
            </p>
            <p>
              Example (4-player, base bid): B = 7, T = 13. Contract team
              takes 7 = push, 8 or more = win. Other team takes 6 = push,
              7 or more = win. A push means no one wins that round.
            </p>
          </section>

          <section className="howto-section">
            <h3>Winning the game</h3>
            <p>
              A game is N rounds — 4 in 4-player, 6 in 6-player — so every
              seat calls exactly once. The team that wins more rounds wins
              the game. Pushes count for neither side. If the round count
              ties, the tiebreak is cumulative hand differential (your hands
              minus your target, summed across rounds), then total hands,
              then a draw.
            </p>
          </section>

          <section className="howto-section howto-aside">
            <h3>Bots, disconnects &amp; reclaiming</h3>
            <p>
              Empty or dropped seats are covered by a rules-legal bot so a
              game never stalls. To reclaim your seat, rejoin the same link
              with the same display name — the bot steps out.
            </p>
          </section>
        </div>
        <footer className="howto-footer">
          <button
            type="button"
            className="howto-close-foot"
            onClick={onClose}
          >
            Got it
          </button>
        </footer>
      </div>
    </div>
  );
}
